import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import imageCompression from "browser-image-compression";
import { AiOutlineEye, AiFillHeart, AiOutlineClose } from "react-icons/ai";
import { FiEdit2 } from "react-icons/fi";

function MyProfile() {
  const [userData, setUserData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatar1File, setAvatar1File] = useState(null);
  const [avatar2File, setAvatar2File] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState("");

  const userId = localStorage.getItem("user_id");
  const userName = localStorage.getItem("user_name");

  const fieldLabels = {
    name: "Name", nickname: "Nickname", profession: "Profession", message: "About",
    dob: "Date of Birth", marital: "Marital Status", children: "Children",
    religion: "Religion", eduqualification: "Educational Qualification",
    school: "School", college: "College", hobbies: "Hobbies",
    bloodgroup: "Blood Group", hometown: "Hometown", currentcity: "Current City",
    nationality: "Nationality", facebook: "Facebook", twitter: "X",
    instagram: "Instagram", linked: "LinkedIn", email: "Email", phone: "Phone", awards: "Awards",
    companyname: "Company Name", foundername: "Founder Name", industry: "Industry",
    products: "Products / Services", startedyear: "Year Started", boardmembers: "Board Members",
    employee: "Number of Employees", gst: "GST Number", website: "Website", 
  };

  useEffect(() => {
    if (!userId) return;
    fetch("/php-api/get-user.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: userId }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setUserData(data.user);
      });
  }, [userId]);

  const handleChange = (e) => {
    setUserData({ ...userData, [e.target.name]: e.target.value });
  };

  const validateImageFile = (file) => {
    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      alert("Please upload an image file (jpg, jpeg, png).");
      return false;
    }
    return true;
  };

  const resizeImageToExactDimensions = (file, width, height) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const newFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(newFile);
            } else {
              reject(new Error("Canvas is empty"));
            }
          },
          file.type,
          0.9
        );
        URL.revokeObjectURL(url);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Image load error"));
      };

      img.src = url;
    });
  };

  const handleImageChange = (fileSetter) => async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!validateImageFile(file)) return;

    try {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 0.2,
        maxWidthOrHeight: 641,
        useWebWorker: true,
        maxIteration: 10,
      });

      const resizedFile = await resizeImageToExactDimensions(compressedFile, 600, 641);
      fileSetter(resizedFile);
    } catch (err) {
      console.error("Image processing error:", err);
      alert("Failed to process image. Please try another image.");
    }
  };

  const handleAvatarChange = handleImageChange(setAvatarFile);
  const handleAvatar1Change = handleImageChange(setAvatar1File);
  const handleAvatar2Change = handleImageChange(setAvatar2File);

  const handleSave = async () => {
    let formData = new FormData();
    Object.keys(userData).forEach((key) => {
      if (key !== "likes" && key !== "views") {
        formData.append(key, userData[key]);
      }
    });
    formData.append("id", userId);

    if (avatarFile) formData.append("avatar", avatarFile);
    if (avatar1File) formData.append("avatar1", avatar1File);
    if (avatar2File) formData.append("avatar2", avatar2File);

    try {
      const res = await fetch("/php-api/update-profile.php", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        alert("Profile updated successfully");
        setEditMode(false);

        // Dispatch custom event to notify header about avatar update

        if (avatarFile) {

          localStorage.setItem('avatar', data.avatar || localStorage.getItem('avatar'));

          window.dispatchEvent(new CustomEvent('profilePictureUpdated', {

            detail: { avatar: data.avatar || localStorage.getItem('avatar') }

          }));

        }
        
        setAvatarFile(null);
        setAvatar1File(null);
        setAvatar2File(null);

        // Refresh data
        fetch("/php-api/get-user.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: userId }),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.success) setUserData(data.user);
          });
      } else {
        alert("Failed to update");
      }
    } catch (error) {
      alert("Error updating profile");
    }
  };

  const openLightbox = (imageUrl) => {
    setCurrentImage(imageUrl);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent scrolling when lightbox is open
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto'; // Re-enable scrolling
  };

  if (!userData) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <p>Loading your profile...</p>
        </div>
        <Footer />
      </>
    );
  }

  const personalFields = [
    "name", "nickname", "profession", "dob", "marital", "children", 
    "religion", "eduqualification", "school", "college", "hobbies", "bloodgroup",
    "hometown", "currentcity", "nationality", "facebook", "twitter",
    "instagram", "linked", "email", "phone", "awards"
  ];

  const professionalFields = [
    "companyname", "foundername", "industry", "products", "startedyear", 
    "boardmembers", "employee", "gst", "website", "facebook", "twitter",
    "instagram", "linked", "phone", "email", "awards"
  ];

  const fullWidthFields = ["message"]; // Fields that should span full width

  const profileType = userData.profile;

  const renderFields = (fields) =>
    fields.map((key) => {
      // Check if this field should be full width
      const isFullWidth = fullWidthFields.includes(key);
      
      return (
        <div 
          key={key} 
          className={`mb-4 ${isFullWidth ? 'col-span-full' : ''}`}
        >
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            {fieldLabels[key] || key}
          </label>
          {editMode ? (
            key === "message" ? (
              <textarea
                name={key}
                value={userData[key] || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md bg-gray-50 focus:outline-none focus:ring"
                rows="4"
              />
            ) : (
              <input
                type="text"
                name={key}
                value={userData[key] || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md bg-gray-50 focus:outline-none focus:ring"
              />
            )
          ) : (
            <p className="text-gray-800">{userData[key] || "NA"}</p>
          )}
        </div>
      );
    });

  return (
    <>
      <Header />
      <div className="page-body-container bg-gray-100">
        <div className="max-w-4xl mx-auto p-6 bg-white shadow-sm rounded-lg relative my-4">
          {!editMode && (
            <button
              onClick={() => setEditMode(true)}
              className="absolute top-4 right-4 flex items-center text-blue-600 hover:text-blue-800"
              title="Edit Profile"
            >
              <FiEdit2 className="text-xl mr-1" />
              Edit Profile
            </button>
          )}

          <div className="flex flex-col items-center mt-4">
            {/* Avatar (mandatory) */}
            <div 
              className="cursor-pointer transform transition-transform hover:scale-105"
              onClick={() => {
                const avatarUrl = avatarFile
                  ? URL.createObjectURL(avatarFile)
                  : userData.avatar?.startsWith("http")
                  ? userData.avatar
                  : `/uploads/${userData.avatar}`;
                openLightbox(avatarUrl);
              }}
            >
              <img
                src={
                  avatarFile
                    ? URL.createObjectURL(avatarFile)
                    : userData.avatar?.startsWith("http")
                    ? userData.avatar
                    : `/uploads/${userData.avatar}`
                }
                alt="avatar"
                className="w-32 h-32 rounded-full object-cover border mb-3"
              />
            </div>
            {editMode && (
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={handleAvatarChange}
                className="mb-4"
              />
            )}

            <h2 className="text-xl font-semibold text-gray-800 mb-1 flex items-center gap-2">
               Welcome, {profileType === "professional" ? userData.companyname || userName : userName}{" "}
              <img
                src={`https://www.giffypedia.com/uploads/${userData.verified || "not-verified.jpg"}`}
                alt={userData.name}
                className="profile-image"
              />
            </h2>
            <div className="flex gap-6 mt-1 text-gray-700">
              <div className="flex items-center gap-1">
                <AiFillHeart className="text-red-500 text-xl" />
                <span>{userData.like || 0} Likes</span>
              </div>
              <div className="flex items-center gap-1">
                <AiOutlineEye className="text-blue-500 text-xl" />
                <span>{userData.view || 0} Views</span>
              </div>
            </div>
          </div>

          <hr className="my-6" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {profileType === "personal"
              ? renderFields([...personalFields, ...fullWidthFields])
              : renderFields([...professionalFields, ...fullWidthFields])}
          </div>

          {/* Show avatar1 & avatar2 images always, inputs only in edit mode */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {(avatar1File || userData.avatar1) && (
              <div className="flex flex-col items-center">
                <div 
                  className="cursor-pointer transform transition-transform hover:scale-105"
                  onClick={() => {
                    const avatar1Url = avatar1File
                      ? URL.createObjectURL(avatar1File)
                      : userData.avatar1?.startsWith("http")
                      ? userData.avatar1
                      : `/uploads/${userData.avatar1}`;
                    openLightbox(avatar1Url);
                  }}
                >
                  <img
                    src={
                      avatar1File
                        ? URL.createObjectURL(avatar1File)
                        : userData.avatar1?.startsWith("http")
                        ? userData.avatar1
                        : `/uploads/${userData.avatar1}`
                    }
                    alt="avatar1"
                    className="sm:w-32 w-full rounded object-cover border mb-2"
                  />
                </div>
                {editMode && (
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleAvatar1Change}
                    className="mb-4"
                  />
                )}
              </div>
            )}

            {(avatar2File || userData.avatar2) && (
              <div className="flex flex-col items-center">
                <div 
                  className="cursor-pointer transform transition-transform hover:scale-105"
                  onClick={() => {
                    const avatar2Url = avatar2File
                      ? URL.createObjectURL(avatar2File)
                      : userData.avatar2?.startsWith("http")
                      ? userData.avatar2
                      : `/uploads/${userData.avatar2}`;
                    openLightbox(avatar2Url);
                  }}
                >
                  <img
                    src={
                      avatar2File
                        ? URL.createObjectURL(avatar2File)
                        : userData.avatar2?.startsWith("http")
                        ? userData.avatar2
                        : `/uploads/${userData.avatar2}`
                    }
                    alt="avatar2"
                    className="sm:w-32 w-full rounded object-cover border mb-2"
                  />
                </div>
                {editMode && (
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleAvatar2Change}
                    className="mb-4"
                  />
                )}
              </div>
            )}
          </div>

          {editMode && (
            <div className="flex justify-end mt-6 gap-4">
              <button
                onClick={handleSave}
                className="bg-[#ef4b3a] text-white px-4 py-2 rounded hover:bg-[#dc402f]"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setEditMode(false);
                  setAvatarFile(null);
                  setAvatar1File(null);
                  setAvatar2File(null);
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        <Footer />
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={closeLightbox}
              className="absolute -right-11 text-white text-3xl hover:text-gray-300 transition-colors"
            >
              <AiOutlineClose className="text-3xl" />
            </button>
            <img
              src={currentImage}
              alt="Enlarged view"
              className="max-w-full max-h-screen object-contain rounded"
            />
          </div>
        </div>
      )}
    </>
  );
}

export default MyProfile;